import { useCallback, useEffect, useRef, useState } from 'react';

const PWA_INSTALLED_KEY = 'tudopets-pwa-installed';

const getIsStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const getIsIos = () => {
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  const isAppleDevice = /iphone|ipad|ipod/i.test(userAgent);
  const isIpadOs = platform === 'MacIntel' && maxTouchPoints > 1;

  return isAppleDevice || isIpadOs;
};

const getStoredInstallFlag = () => {
  try {
    return localStorage.getItem(PWA_INSTALLED_KEY) === 'true';
  } catch {
    return false;
  }
};

const setStoredInstallFlag = () => {
  try {
    localStorage.setItem(PWA_INSTALLED_KEY, 'true');
  } catch {
    // ignore storage failures
  }
};

export function usePwaInstall() {
  const deferredPromptRef = useRef(null);
  const [canPromptInstall, setCanPromptInstall] = useState(false);
  const [isStandalone, setIsStandalone] = useState(getIsStandalone);
  const [isAppInstalled, setIsAppInstalled] = useState(
    () => getIsStandalone() || getStoredInstallFlag()
  );
  const [isIos] = useState(getIsIos);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');

    const detectInstalledApp = async () => {
      if (getIsStandalone()) {
        setIsAppInstalled(true);
        return;
      }

      if (getStoredInstallFlag()) {
        setIsAppInstalled(true);
        return;
      }

      if (!('getInstalledRelatedApps' in navigator)) return;

      try {
        const relatedApps = await navigator.getInstalledRelatedApps();
        if (relatedApps.length > 0) {
          setIsAppInstalled(true);
          setStoredInstallFlag();
        }
      } catch {
        // API unavailable or blocked
      }
    };

    const handleDisplayModeChange = () => {
      const standalone = getIsStandalone();
      setIsStandalone(standalone);
      if (standalone) {
        setIsAppInstalled(true);
      }
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setCanPromptInstall(true);
    };

    const handleAppInstalled = () => {
      deferredPromptRef.current = null;
      setCanPromptInstall(false);
      setIsStandalone(true);
      setIsAppInstalled(true);
      setStoredInstallFlag();
    };

    detectInstalledApp();
    mediaQuery.addEventListener('change', handleDisplayModeChange);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const prompt = deferredPromptRef.current;

    if (!prompt) {
      return { outcome: 'unavailable' };
    }

    await prompt.prompt();
    const choice = await prompt.userChoice;

    deferredPromptRef.current = null;
    setCanPromptInstall(false);

    if (choice.outcome === 'accepted') {
      setIsStandalone(getIsStandalone());
      setIsAppInstalled(true);
      setStoredInstallFlag();
    }

    return choice;
  }, []);

  return {
    canPromptInstall,
    isStandalone,
    isAppInstalled,
    isIos,
    promptInstall,
  };
}
